const DEFAULT_SETTINGS = {
  balance_critical: 5000,
  balance_warning: 15000,
  coverage_critical_days: 3,
  stacking_warning_pct: 35,
  stacking_high_positions: 3,
  stacking_moderate_positions: 2,
  low_balance_watch: 10000,
};

export function analyzeTransactions(txns, settings = {}) {
  const cfg = { ...DEFAULT_SETTINGS, ...settings };
  const today = new Date();

  // Filter out excluded transactions for analysis
  const activeTxns = txns.filter((t) => t.flag !== "exclude");

  const getDateRange = (days) => {
    const d = new Date(today);
    d.setDate(today.getDate() - days);
    return d.toISOString().split("T")[0];
  };

  const filterTxns = (days) => activeTxns.filter((t) => t.date >= getDateRange(days));

  // Determine effective type for a transaction based on flags
  const isEffectiveRevenue = (t) => {
    if (t.flag === "revenue") return true;
    if (t.flag === "not_revenue") return false;
    if (t.flag === "lender") return false;
    return t.type === "credit" && !t.isLoan;
  };

  const isEffectiveLoan = (t) => {
    if (t.flag === "lender") return true;
    if (t.flag === "revenue") return false;
    if (t.flag === "not_revenue") return false;
    return t.isLoan;
  };

  const summarize = (arr) => ({
    totalCredits: arr.filter((t) => isEffectiveRevenue(t)).reduce((s, t) => s + Math.abs(t.amount), 0),
    totalDebits: Math.abs(arr.filter((t) => t.type === "debit" && !isEffectiveLoan(t) && t.flag !== "revenue").reduce((s, t) => s + t.amount, 0)),
    loanPayments: Math.abs(arr.filter((t) => isEffectiveLoan(t) && t.type === "debit").reduce((s, t) => s + t.amount, 0)),
    loanProceeds: arr.filter((t) => isEffectiveLoan(t) && t.type === "credit").reduce((s, t) => s + t.amount, 0),
    txnCount: arr.length,
    avgDailyBalance: arr.length ? arr.reduce((s, t) => s + t.balance, 0) / arr.length : 0,
    minBalance: arr.length ? Math.min(...arr.map((t) => t.balance)) : 0,
  });

  const daily = summarize(filterTxns(1));
  const weekly = summarize(filterTxns(7));
  const monthly = summarize(filterTxns(30));

  const getCalendarMonth = (monthsAgo) => {
    const d = new Date(today.getFullYear(), today.getMonth() - monthsAgo, 1);
    const start = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
    const endDate = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const end = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, "0")}-${String(endDate.getDate()).padStart(2, "0")}`;
    const label = d.toLocaleString("default", { month: "long", year: "numeric" });
    return { start, end, label };
  };

  const summarizeRange = (start, end) =>
    summarize(activeTxns.filter((t) => t.date >= start && t.date <= end));

  // Month to date
  const mtdStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
  const mtdEnd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const mtdLabel = today.toLocaleString("default", { month: "long", year: "numeric" }) + " MTD";
  const monthToDate = { ...summarizeRange(mtdStart, mtdEnd), label: mtdLabel };

  const prevMonth1 = getCalendarMonth(1);
  const prevMonth2 = getCalendarMonth(2);
  const prevMonth3 = getCalendarMonth(3);

  const calMonth1 = { ...summarizeRange(prevMonth1.start, prevMonth1.end), label: prevMonth1.label };
  const calMonth2 = { ...summarizeRange(prevMonth2.start, prevMonth2.end), label: prevMonth2.label };
  const calMonth3 = { ...summarizeRange(prevMonth3.start, prevMonth3.end), label: prevMonth3.label };

  const loanEvents = activeTxns.filter((t) => isEffectiveLoan(t));
  const activeLenders = [...new Set(
    loanEvents.filter((t) => t.type === "debit").map((t) => t.name.replace(/ FACTOR PMT| REPAY/, ""))
  )];
  const loanFunding = loanEvents.filter((t) => t.type === "credit");
  const dailyFactorTotal = Math.abs(
    loanEvents.filter((t) => t.type === "debit" && t.date >= getDateRange(30)).reduce((s, t) => s + t.amount, 0)
  ) / 30;

  const currentBalance = txns[0]?.balance || 0; // Use all txns for balance, not just active

  // Alerts use configurable thresholds
  const balanceAlerts = [];
  if (currentBalance < cfg.balance_critical) {
    balanceAlerts.push({ level: "CRITICAL", msg: `Balance below $${cfg.balance_critical.toLocaleString()} — ACH bounce risk is HIGH` });
  } else if (currentBalance < cfg.balance_warning) {
    balanceAlerts.push({ level: "WARNING", msg: `Balance below $${cfg.balance_warning.toLocaleString()} — monitor daily for ACH coverage` });
  }
  if (currentBalance < dailyFactorTotal * cfg.coverage_critical_days) {
    balanceAlerts.push({ level: "CRITICAL", msg: `Balance covers less than ${cfg.coverage_critical_days} days of factor payments` });
  }
  if (monthly.loanPayments / (monthly.totalCredits || 1) > cfg.stacking_warning_pct / 100) {
    balanceAlerts.push({ level: "WARNING", msg: `Loan repayments exceed ${cfg.stacking_warning_pct}% of monthly revenue — stacking risk` });
  }

  return {
    daily, weekly, monthly,
    monthToDate, calMonth1, calMonth2, calMonth3,
    loanEvents, activeLenders, loanFunding,
    dailyFactorTotal, currentBalance, balanceAlerts,
    settings: cfg,
  };
}
